import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authService, SignupData, LoginData, AuthResponse, UserStatus } from '../services/authService'
import { User } from '../stores/userStore'

interface AuthContextType {
  user: User | null
  loading: boolean
  userStatus: UserStatus | null
  signUp: (data: SignupData) => Promise<AuthResponse>
  signIn: (data: LoginData) => Promise<AuthResponse>
  signOut: () => Promise<void>
  updateProfile: (uid: string, updates: any) => Promise<AuthResponse>
  resetPassword: (email: string) => Promise<AuthResponse>
  checkUserStatus: (uid: string, autoImport?: boolean, forceRefresh?: boolean) => Promise<UserStatus>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null)
  const [lastStatusCheck, setLastStatusCheck] = useState<number>(0)

  // Helper function to convert backend user to frontend User type
  const convertBackendUserToUser = (backendUser: any): User => {
    return {
      id: backendUser.uid,
      uid: backendUser.uid,
      name: backendUser.name,
      email: backendUser.email,
      birthData: backendUser.profile?.birthData || backendUser.birthData,
      contacts: backendUser.contacts || [],
      astroProfile: backendUser.astroProfile,
      doshaProfile: backendUser.doshaProfile,
      createdAt: backendUser.createdAt || new Date(),
      updatedAt: backendUser.updatedAt || new Date()
    }
  }

  // Check for existing auth on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        const savedUser = localStorage.getItem('user')
        
        if (token && savedUser) {
          // Try to get fresh user data from backend
          try {
            const response = await authService.getCurrentUser()
            if (response.success && response.user) {
              // Convert backend user to frontend User type
              const userWithBirthData = convertBackendUserToUser(response.user)
              setUser(userWithBirthData)
              localStorage.setItem('user', JSON.stringify(userWithBirthData))
            } else {
              // If backend call fails, use localStorage data
              const user = JSON.parse(savedUser)
              setUser(user)
              console.log('Using cached user data from localStorage')
            }
          } catch (error) {
            console.error('Error getting current user from backend:', error)
            // Fallback to localStorage
            const user = JSON.parse(savedUser)
            setUser(user)
            console.log('Using cached user data from localStorage due to backend error')
          }
        } else if (token && !savedUser) {
          // Have token but no cached user - try to get from backend
          try {
            const response = await authService.getCurrentUser()
            if (response.success && response.user) {
              // Convert backend user to frontend User type
              const userWithBirthData = convertBackendUserToUser(response.user)
              setUser(userWithBirthData)
              localStorage.setItem('user', JSON.stringify(userWithBirthData))
            } else {
              // Clear invalid token
              localStorage.removeItem('authToken')
              setUser(null)
            }
          } catch (error) {
            console.error('Error getting current user from backend:', error)
            // Clear invalid token
            localStorage.removeItem('authToken')
            setUser(null)
          }
        }
      } catch (error) {
        console.error('Auth check error:', error)
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  const signUp = async (data: SignupData): Promise<AuthResponse> => {
    try {
      setLoading(true)
      const response = await authService.signUp(data)
      
      if (response.success && response.user) {
        // Convert backend user to frontend User type
        const userWithBirthData = convertBackendUserToUser(response.user)
        setUser(userWithBirthData)
        
        // Store JWT token if provided, otherwise fallback to UID for development
        if (response.token) {
          localStorage.setItem('authToken', response.token)
        } else {
          localStorage.setItem('authToken', response.user.uid) // Fallback for development
        }
        
        localStorage.setItem('user', JSON.stringify(userWithBirthData))
      }
      
      return response
    } catch (error) {
      console.error('Signup error:', error)
      return {
        success: false,
        error: 'Failed to create account'
      }
    } finally {
      setLoading(false)
    }
  }

  const signIn = async (data: LoginData): Promise<AuthResponse> => {
    try {
      setLoading(true)
      const response = await authService.signIn(data)
      
      if (response.success && response.user) {
        // Convert backend user to frontend User type
        const userWithBirthData = convertBackendUserToUser(response.user)
        setUser(userWithBirthData)
        
        // Store JWT token if provided, otherwise fallback to UID for development
        if (response.token) {
          localStorage.setItem('authToken', response.token)
        } else {
          localStorage.setItem('authToken', response.user.uid) // Fallback for development
        }
        
        localStorage.setItem('user', JSON.stringify(userWithBirthData))
      }
      
      return response
    } catch (error) {
      console.error('Signin error:', error)
      return {
        success: false,
        error: 'Failed to sign in'
      }
    } finally {
      setLoading(false)
    }
  }

  const signOut = async (): Promise<void> => {
    try {
      await authService.signOut()
    } catch (error) {
      console.error('Signout error:', error)
    } finally {
      setUser(null)
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
    }
  }

  const updateProfile = async (uid: string, updates: any): Promise<AuthResponse> => {
    try {
      const response = await authService.updateProfile(uid, updates)
      
      if (response.success && response.user) {
        const userWithBirthData = convertBackendUserToUser(response.user)
        setUser(userWithBirthData)
        localStorage.setItem('user', JSON.stringify(userWithBirthData))
      }
      
      return response
    } catch (error) {
      console.error('Update profile error:', error)
      return {
        success: false,
        error: 'Failed to update profile'
      }
    }
  }

  const resetPassword = async (email: string): Promise<AuthResponse> => {
    try {
      return await authService.resetPassword(email)
    } catch (error) {
      console.error('Reset password error:', error)
      return {
        success: false,
        error: 'Failed to reset password'
      }
    }
  }

  const checkUserStatus = async (uid: string, autoImport: boolean = false, forceRefresh: boolean = false): Promise<UserStatus> => {
    // Add cooldown to prevent too frequent requests (unless force refresh)
    const now = Date.now()
    const cooldownPeriod = forceRefresh ? 1000 : 5000 // 1 second for force refresh, 5 seconds normal
    if (!forceRefresh && now - lastStatusCheck < cooldownPeriod) {
      console.log('Status check skipped due to cooldown period')
      return userStatus || {
        success: false,
        uid,
        isOnline: false,
        astroAgentsLive: 0,
        totalCharts: 0,
        message: 'Status check in cooldown'
      }
    }
    
    setLastStatusCheck(now)
    
    try {
      const status = await authService.getUserStatus(uid, autoImport)
      setUserStatus(status)
      
      // Log Astro Agents status for debugging
      if (status.success) {
        console.log(`User status: ${status.isOnline ? 'Online' : 'Offline'}, Astro Agents: ${status.astroAgentsLive}/5 live`)
      }
      
      return status
    } catch (error) {
      console.error('Check user status error:', error)
      const offlineStatus: UserStatus = {
        success: false,
        uid,
        isOnline: false,
        astroAgentsLive: 0,
        totalCharts: 0,
        retryAttempts: 1,
        message: 'Connection failed. Please check your internet connection and try again.'
      }
      setUserStatus(offlineStatus)
      return offlineStatus
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    userStatus,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    checkUserStatus
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
} 